// frontend/src/context/TenantContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { getTenantFromUrl, mapTenantCode } from "../utils/tenant";
import { isAdminStorefrontPreview } from "../utils/storefrontPreview";
import { STOREFRONT_TEMPLATE_IDS } from "../utils/storefrontTheme";
import api from "../services/api";

// ✅ Create the context
const TenantContext = createContext();

// ✅ Export the hook as a named export
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};

// ✅ Export the Provider as a named export
export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenantCode, setTenantCode] = useState(null);
  const hasLoaded = useRef(false);
  const previewSettings = useRef(null);
  const mergePreviewSettings = (baseSettings) =>
    previewSettings.current
      ? { ...baseSettings, ...previewSettings.current }
      : baseSettings;
  const updateSettings = (patch) => {
    setSettings((currentSettings) => ({
      ...currentSettings,
      ...patch,
    }));
  };
  const updateTenant = (patch) => {
    setTenant((currentTenant) =>
      currentTenant ? { ...currentTenant, ...patch } : currentTenant,
    );
  };

  useEffect(() => {
    if (hasLoaded.current) return;
    hasLoaded.current = true;
    loadTenant();
  }, []);

  useEffect(() => {
    const isAdminPreview = isAdminStorefrontPreview();
    if (!isAdminPreview || window.parent === window) return undefined;

    const handlePreviewSettings = (event) => {
      if (
        event.source !== window.parent ||
        event.origin !== window.location.origin ||
        event.data?.type !== "tooraflex:storefront-preview" ||
        typeof event.data.configuration !== "object" ||
        event.data.configuration === null ||
        Array.isArray(event.data.configuration)
      ) {
        return;
      }

      const template = event.data.template;
      if (
        typeof template !== "string" ||
        !STOREFRONT_TEMPLATE_IDS.includes(template)
      ) {
        return;
      }

      previewSettings.current = {
        storefrontTemplate: template,
        storefrontThemeConfig: event.data.configuration,
      };
      setSettings((currentSettings) => ({
        ...currentSettings,
        ...previewSettings.current,
      }));
    };

    window.addEventListener("message", handlePreviewSettings);
    return () => window.removeEventListener("message", handlePreviewSettings);
  }, []);

  const loadTenant = async () => {
    try {
      const rawTenantCode = getTenantFromUrl();
      const mappedTenantCode = mapTenantCode(rawTenantCode);

      console.log("🔄 Loading tenant with raw code:", rawTenantCode);
      console.log("🔄 Mapped tenant code:", mappedTenantCode);

      if (
        !mappedTenantCode ||
        mappedTenantCode === "null" ||
        mappedTenantCode === "undefined"
      ) {
        console.error("❌ Invalid tenant code:", mappedTenantCode);
        setError("Invalid tenant code");
        setLoading(false);
        return;
      }

      // Store tenant code
      setTenantCode(mappedTenantCode);
      localStorage.setItem("tenantCode", mappedTenantCode);

      // ✅ Use the working endpoint - same as old version
      console.log(`📡 Fetching tenant config for: ${mappedTenantCode}`);
      
      let tenantData = null;
      
      try {
        // Try the original working endpoint first
        const response = await api.get(
          `/api/tenants/config/${mappedTenantCode}`,
          { params: { _config: Date.now() } },
        );
        if (response.data) {
          tenantData = response.data;
          console.log("✅ Tenant loaded from /api/tenants/config:", tenantData);
        }
      } catch (err) {
        console.log("ℹ️ /api/tenants/config failed, trying alternative...");
      }

      // If that fails, try the public endpoint
      if (!tenantData) {
        try {
          const response = await api.get(`/api/public/tenant/${mappedTenantCode}`);
          if (response.data && response.data.success) {
            tenantData = response.data.tenant;
            console.log("✅ Tenant loaded from /api/public/tenant:", tenantData);
          }
        } catch (err) {
          console.log("ℹ️ /api/public/tenant failed, trying fallback...");
        }
      }

      // If that fails, try the tenant endpoint
      if (!tenantData) {
        try {
          const response = await api.get(`/api/tenants/${mappedTenantCode}`);
          if (response.data && response.data.success) {
            tenantData = response.data.tenant;
            console.log("✅ Tenant loaded from /api/tenants:", tenantData);
          }
        } catch (err) {
          console.log("ℹ️ /api/tenants failed, using fallback...");
        }
      }

      // If all endpoints failed, use fallback/default
      if (!tenantData) {
        console.log("ℹ️ Using fallback tenant data");
        tenantData = {
          success: true,
          TenantID: null,
          TenantCode: mappedTenantCode,
          BusinessName: mappedTenantCode.charAt(0).toUpperCase() + mappedTenantCode.slice(1).replace(/-/g, ' '),
          BusinessEmail: null,
          BusinessPhone: null,
          Logo: null,
          Favicon: "/icon-shoping.ico",
          PrimaryColor: "#2c7da0",
          SecondaryColor: "#61a5c2",
          TertiaryColor: null,
          AccentColor: null,
          TextColor: "#2d3e50",
          HeaderFooterColor: null,
          AddressLine1: null,
          AddressLine2: null,
          City: null,
          State: null,
          PostalCode: null,
          Country: "South Africa",
          FullAddress: null,
          AlternatePhone: null,
          WhatsAppNumber: null,
          BusinessHours: null,
          SocialFacebook: null,
          SocialInstagram: null,
          SocialTwitter: null,
          SocialLinkedIn: null,
          Domain: null,
          DomainExtension: ".co.za",
          GoogleMapsEmbed: null,
          PrivacyContent: null,
          AboutUsContent: null,
          TermsContent: null,
          ShippingContent: null,
          ReturnsContent: null,
          CancellationContent: null,
          FAQContent: null,
          IsActive: true,
          settings: {
            homepageDisplayType: "both",
            domainExtension: ".co.za",
            storefrontTemplate: "classic",
          }
        };
      }

      // Set tenant data
      setTenant(tenantData);
      // Extract settings if they exist in the response
      if (tenantData.settings) {
        setSettings(mergePreviewSettings(tenantData.settings));
        console.log("⚙️ Settings loaded:", tenantData.settings);
      } else {
        // Set default settings if none provided
        setSettings(mergePreviewSettings({
          homepageDisplayType: "both",
          domainExtension: tenantData.DomainExtension || ".co.za",
          storefrontTemplate: "classic",
        }));
      }

      // Apply theme colors and favicon from tenant
      applyTenantTheme(tenantData);
      applyTenantFavicon(tenantData);

      // Update document title
      document.title = tenantData.BusinessName || "TF Online";

      // Store tenant data in localStorage for offline access
      localStorage.setItem("tenantData", JSON.stringify(tenantData));
    } catch (err) {
      console.error("❌ Failed to load tenant:", err);
      setError(err.message);

      // Try to load from localStorage as fallback
      try {
        const cachedTenant = localStorage.getItem("tenantData");
        if (cachedTenant) {
          const parsedTenant = JSON.parse(cachedTenant);
          console.log("💾 Using cached tenant data:", parsedTenant);
          setTenant(parsedTenant);
          setSettings(
            mergePreviewSettings(parsedTenant.settings || {
              homepageDisplayType: "both",
              domainExtension: parsedTenant.DomainExtension || ".co.za",
              storefrontTemplate: "classic",
            }),
          );
          applyTenantTheme(parsedTenant);
          applyTenantFavicon(parsedTenant);
        }
      } catch (cacheErr) {
        console.error("❌ Failed to load cached tenant:", cacheErr);
      }

      // Don't show error for 404, just use default
      if (err.response?.status === 404) {
        console.log("ℹ️ Tenant not found on server, using local config");
        const defaultTenant = {
          success: true,
          TenantID: null,
          TenantCode: getTenantFromUrl(),
          BusinessName: "TF Online",
          BusinessEmail: null,
          BusinessPhone: null,
          Logo: null,
          Favicon: "/icon-shoping.ico",
          PrimaryColor: "#2c7da0",
          SecondaryColor: "#61a5c2",
          TertiaryColor: null,
          AccentColor: null,
          TextColor: "#2d3e50",
          HeaderFooterColor: null,
          AddressLine1: null,
          AddressLine2: null,
          City: null,
          State: null,
          PostalCode: null,
          Country: "South Africa",
          FullAddress: null,
          AlternatePhone: null,
          WhatsAppNumber: null,
          BusinessHours: null,
          SocialFacebook: null,
          SocialInstagram: null,
          SocialTwitter: null,
          SocialLinkedIn: null,
          Domain: null,
          DomainExtension: ".co.za",
          GoogleMapsEmbed: null,
          PrivacyContent: null,
          AboutUsContent: null,
          TermsContent: null,
          ShippingContent: null,
          ReturnsContent: null,
          CancellationContent: null,
          FAQContent: null,
          IsActive: true,
          settings: {
            homepageDisplayType: "both",
            domainExtension: ".co.za",
            storefrontTemplate: "classic",
          },
        };
        setTenant(defaultTenant);
        setSettings(mergePreviewSettings(defaultTenant.settings));
        localStorage.setItem("tenantData", JSON.stringify(defaultTenant));
        applyTenantTheme(defaultTenant);
        applyTenantFavicon(defaultTenant);
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ Helper function to apply tenant theme
  const applyTenantTheme = (tenantData) => {
    if (!tenantData) return;

    const root = document.documentElement;

    if (tenantData.PrimaryColor) {
      root.style.setProperty("--primary-color", tenantData.PrimaryColor);
      // Also update meta theme-color
      updateMetaThemeColor(tenantData.PrimaryColor);
    }
    if (tenantData.SecondaryColor) {
      root.style.setProperty("--secondary-color", tenantData.SecondaryColor);
    }
    if (tenantData.AccentColor) {
      root.style.setProperty("--accent-color", tenantData.AccentColor);
    }
    if (tenantData.HeaderFooterColor) {
      root.style.setProperty(
        "--header-footer-color",
        tenantData.HeaderFooterColor,
      );
    }
  };

  // ✅ Helper function to apply tenant favicon
  const applyTenantFavicon = (tenantData) => {
    if (!tenantData) return;

    // Determine favicon URL
    let faviconUrl = tenantData.Favicon || "/icon-shoping.ico";

    // If logo exists (base64 from API), use it as favicon
    if (tenantData.Logo) {
      faviconUrl = tenantData.Logo;
    }
    // Otherwise, use the logo endpoint URL
    else if (tenantData.TenantCode) {
      faviconUrl = `/api/tenant-logo/${tenantData.TenantCode}`;
    }

    console.log("🖼️ Setting favicon to:", faviconUrl);

    // Update standard favicon
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = faviconUrl;

    // Update apple-touch-icon
    let appleLink = document.querySelector("link[rel~='apple-touch-icon']");
    if (!appleLink) {
      appleLink = document.createElement("link");
      appleLink.rel = "apple-touch-icon";
      document.head.appendChild(appleLink);
    }
    appleLink.href = faviconUrl;

    // Update shortcut icon (for older browsers)
    let shortcutLink = document.querySelector("link[rel~='shortcut icon']");
    if (!shortcutLink) {
      shortcutLink = document.createElement("link");
      shortcutLink.rel = "shortcut icon";
      document.head.appendChild(shortcutLink);
    }
    shortcutLink.href = faviconUrl;
  };

  // ✅ Helper function to update meta theme-color
  const updateMetaThemeColor = (color) => {
    let metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (!metaThemeColor) {
      metaThemeColor = document.createElement("meta");
      metaThemeColor.name = "theme-color";
      document.head.appendChild(metaThemeColor);
    }
    metaThemeColor.content = color;
  };

  // ✅ Function to refresh tenant data
  const refreshTenant = async () => {
    setLoading(true);
    setError(null);
    await loadTenant();
  };

  // ✅ Return the context value with all needed properties
  return (
    <TenantContext.Provider
      value={{
        tenant,
        settings,
        updateSettings,
        updateTenant,
        loading,
        error,
        tenantCode,
        refreshTenant,
        // Convenience getters
        getBusinessName: () => tenant?.BusinessName || "TF Online",
        getPrimaryColor: () => tenant?.PrimaryColor || "#2c7da0",
        getSecondaryColor: () => tenant?.SecondaryColor || "#61a5c2",
        getDomainExtension: () => tenant?.DomainExtension || ".co.za",
        getLogo: () => tenant?.Logo || null,
        getFavicon: () => {
          if (tenant?.Logo) return tenant.Logo;
          if (tenant?.TenantCode)
            return `/api/tenant-logo/${tenant.TenantCode}`;
          return tenant?.Favicon || "/icon-shoping.ico";
        },
        isTenantLoaded: () => !!tenant,
        isActive: () => tenant?.IsActive !== false,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

// ✅ Default export for backward compatibility
export default TenantContext;